import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, X, Maximize2, Loader2, Check, XIcon, ChevronRight, Pencil, MapPin, Tag } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';

// ---- Session Storage Helpers ----
const STORAGE_KEY_MESSAGES = 'tw1-assist-messages';
const STORAGE_KEY_SESSION = 'tw1-assist-session';
const STORAGE_KEY_GREETED = 'tw1-assist-greeted';

function loadFromSession(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}
function saveToSession(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ---- Quick Complete Inline ----
function QuickCompleteInline({ item, authAxios, onDone }) {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleComplete = async () => {
    setLoading(true);
    try {
      const api = authAxios();
      await api.post(`/compliance-items/${item.id}/complete`, { notes: notes || 'Afgerond via AI Assistent' });
      setDone(true);
      toast.success('Item afgerond');
      if (onDone) onDone(item.id);
    } catch (err) {
      toast.error('Afronden mislukt');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`quick-complete-done-${item.id}`}>
        <Check className="w-3.5 h-3.5" /> Afgerond
      </div>
    );
  }

  return (
    <div className="mt-1.5 flex items-center gap-1.5" data-testid={`quick-complete-form-${item.id}`}>
      <input
        type="text"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Notities (optioneel)..."
        className="flex-1 px-2 py-1 text-xs rounded-md border border-slate-200 dark:border-[#30363D] bg-slate-50 dark:bg-[#161B22] text-slate-800 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
        data-testid={`quick-complete-notes-${item.id}`}
      />
      <button
        onClick={handleComplete}
        disabled={loading}
        className="px-2 py-1 text-xs font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        data-testid={`quick-complete-confirm-${item.id}`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Bevestig'}
      </button>
    </div>
  );
}

// ---- Reference Card with Quick Actions ----
function ReferenceCard({ ref_item, navigate, onClose, authAxios }) {
  const [showComplete, setShowComplete] = useState(false);
  const [completed, setCompleted] = useState(false);

  const typeMap = {
    entry: { label: 'Kennisbank', path: (id) => `/kennisbank/${id}`, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
    compliance: { label: 'Compliance', path: () => '/compliance', color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
    location: { label: 'Locatie', path: (id) => `/locatie/${id}`, color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' },
    file: { label: 'Bestand', path: () => '/kennisbank?tab=bestanden', color: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20' },
  };
  const config = typeMap[ref_item.type] || typeMap.entry;
  const quickActions = ref_item.quick_actions || [];

  const handleNavClick = () => {
    navigate(config.path(ref_item.id));
    if (onClose) onClose();
  };

  return (
    <div className={`w-full rounded-lg border border-slate-200/80 dark:border-[#30363D] ${config.color} transition-colors duration-150`}
         data-testid={`assist-reference-${ref_item.type}-${ref_item.id}`}>
      <button
        onClick={handleNavClick}
        className="flex items-center gap-2 w-full text-left px-3 py-2 hover:opacity-80 transition-opacity"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{config.label}</span>
        <span className="flex-1 text-sm font-medium truncate text-slate-900 dark:text-[#C9D1D9]">{ref_item.title}</span>
        {ref_item.subtitle && <span className="text-xs text-slate-500 dark:text-[#8B949E] truncate max-w-[120px]">{ref_item.subtitle}</span>}
        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
      </button>

      {/* Quick Action Buttons */}
      {quickActions.length > 0 && !completed && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {quickActions.includes('afronden') && !showComplete && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowComplete(true); }}
              className="px-2 py-0.5 text-[11px] font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
              data-testid={`quick-action-afronden-${ref_item.id}`}
            >
              Afronden
            </button>
          )}
          {quickActions.includes('open') && (
            <button
              onClick={(e) => { e.stopPropagation(); handleNavClick(); }}
              className="px-2 py-0.5 text-[11px] font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              data-testid={`quick-action-open-${ref_item.id}`}
            >
              Open
            </button>
          )}
          {quickActions.includes('ga_naar_locatie') && (
            <button
              onClick={(e) => { e.stopPropagation(); handleNavClick(); }}
              className="px-2 py-0.5 text-[11px] font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
              data-testid={`quick-action-ga-naar-${ref_item.id}`}
            >
              Ga naar locatie
            </button>
          )}
        </div>
      )}

      {/* Inline Complete Form */}
      {showComplete && !completed && (
        <div className="px-3 pb-2">
          <QuickCompleteInline
            item={ref_item}
            authAxios={authAxios}
            onDone={() => { setCompleted(true); setShowComplete(false); }}
          />
        </div>
      )}

      {completed && (
        <div className="px-3 pb-2 flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
          <Check className="w-3.5 h-3.5" /> Afgerond
        </div>
      )}
    </div>
  );
}

// ---- Action Card (Level 2) ----
function ActionCard({ proposal, onConfirm, onCancel, loading, authAxios }) {
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState({ ...proposal.params });
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!editing) return;
    const api = authAxios();
    api.get('/locations').then(res => setLocations(res.data || [])).catch(() => {});
    api.get('/categories').then(res => setCategories(res.data || [])).catch(() => {});
  }, [editing, authAxios]);

  const updateField = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  const handleSave = () => {
    onConfirm({ ...proposal, params: fields });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="mt-2 p-3 rounded-lg border-2 border-[#2E75B6]/30 dark:border-[#2E75B6]/40 bg-blue-50/50 dark:bg-[#2E75B6]/10 space-y-2.5" data-testid="assist-action-edit">
        <p className="text-xs font-semibold text-slate-500 dark:text-[#8B949E] uppercase tracking-wider">Aanpassen</p>

        {/* Title */}
        {fields.title !== undefined && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E]">Titel</label>
            <input
              type="text" value={fields.title || ''} onChange={e => updateField('title', e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
              data-testid="action-edit-title"
            />
          </div>
        )}

        {/* Location */}
        {(fields.location_id !== undefined || proposal.action === 'create_entry') && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E] flex items-center gap-1"><MapPin className="w-3 h-3" />Locatie</label>
            <select
              value={fields.location_id || ''} onChange={e => updateField('location_id', e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
              data-testid="action-edit-location"
            >
              <option value="">Kies locatie...</option>
              {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </div>
        )}

        {/* Category (entries only) */}
        {proposal.action === 'create_entry' && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E] flex items-center gap-1"><Tag className="w-3 h-3" />Categorie</label>
            <select
              value={fields.category || ''} onChange={e => updateField('category', e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
              data-testid="action-edit-category"
            >
              <option value="">Kies categorie...</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.display_name}</option>)}
            </select>
          </div>
        )}

        {/* Content */}
        {fields.content !== undefined && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E]">Inhoud</label>
            <textarea
              value={fields.content || ''} onChange={e => updateField('content', e.target.value)} rows={2}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40 resize-none"
              data-testid="action-edit-content"
            />
          </div>
        )}

        {/* Type (compliance only) */}
        {proposal.action === 'create_compliance_item' && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E]">Type</label>
            <select
              value={fields.type || 'keuring'} onChange={e => updateField('type', e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
              data-testid="action-edit-type"
            >
              {['keuring','contract','onderhoud','voertuig','vergunning','betaling'].map(t =>
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              )}
            </select>
          </div>
        )}

        {/* Due date (compliance only) */}
        {fields.due_date !== undefined && (
          <div>
            <label className="text-[11px] text-slate-500 dark:text-[#8B949E]">Deadline</label>
            <input
              type="date"
              value={(fields.due_date || '').slice(0, 10)}
              onChange={e => updateField('due_date', e.target.value ? new Date(e.target.value).toISOString() : '')}
              className="w-full mt-0.5 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] focus:outline-none focus:ring-1 focus:ring-[#2E75B6]/40"
              data-testid="action-edit-due-date"
            />
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={handleSave} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-[#2E75B6] hover:bg-[#2563a0] disabled:opacity-50 transition-colors"
            data-testid="action-edit-save"
          >
            <Check className="w-3.5 h-3.5" /> Opslaan
          </button>
          <button onClick={() => { setFields({ ...proposal.params }); setEditing(false); }} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 dark:text-[#8B949E] bg-slate-100 dark:bg-[#21262D] hover:bg-slate-200 dark:hover:bg-[#30363D] transition-colors"
            data-testid="action-edit-cancel"
          >
            Annuleren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 p-3 rounded-lg border-2 border-[#2E75B6]/30 dark:border-[#2E75B6]/40 bg-blue-50/50 dark:bg-[#2E75B6]/10" data-testid="assist-action-card">
      <p className="text-sm text-slate-700 dark:text-[#C9D1D9] mb-3">{proposal.description}</p>

      {/* Show proposed params summary */}
      <div className="text-xs text-slate-500 dark:text-[#8B949E] space-y-0.5 mb-3">
        {proposal.params.title && <div>Titel: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.title}</span></div>}
        {proposal.params.location_name && <div>Locatie: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.location_name}</span></div>}
        {proposal.params.location_id && !proposal.params.location_name && <div>Locatie: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.location_id}</span></div>}
        {proposal.params.category && <div>Categorie: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.category}</span></div>}
        {proposal.params.type && <div>Type: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.type}</span></div>}
        {proposal.params.due_date && <div>Deadline: <span className="text-slate-700 dark:text-[#C9D1D9]">{proposal.params.due_date.slice(0,10)}</span></div>}
      </div>

      <div className="flex gap-2">
        <button onClick={() => onConfirm(proposal)} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-[#2E75B6] hover:bg-[#2563a0] disabled:opacity-50 transition-colors"
          data-testid="assist-action-confirm"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Opslaan
        </button>
        <button onClick={() => setEditing(true)} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 dark:text-[#8B949E] bg-slate-100 dark:bg-[#21262D] hover:bg-slate-200 dark:hover:bg-[#30363D] transition-colors"
          data-testid="assist-action-edit"
        >
          <Pencil className="w-3.5 h-3.5" /> Aanpassen
        </button>
        <button onClick={onCancel} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 dark:text-[#8B949E] bg-slate-100 dark:bg-[#21262D] hover:bg-slate-200 dark:hover:bg-[#30363D] transition-colors"
          data-testid="assist-action-reject"
        >
          <XIcon className="w-3.5 h-3.5" /> Annuleren
        </button>
      </div>
    </div>
  );
}

// ---- Message Bubble ----
function MessageBubble({ msg, navigate, onClose, authAxios }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`assist-message-${msg.role}`}>
      <div className={`max-w-[85%] ${isUser
        ? 'bg-[#2E75B6] text-white rounded-2xl rounded-br-md px-4 py-2.5'
        : 'bg-white dark:bg-[#161B22] border border-slate-200/80 dark:border-[#30363D] text-slate-800 dark:text-[#C9D1D9] rounded-2xl rounded-bl-md px-4 py-2.5'
      }`}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
        {msg.references && msg.references.length > 0 && (
          <div className="mt-2.5 space-y-1.5">
            {msg.references.map((ref, i) => (
              <ReferenceCard key={i} ref_item={ref} navigate={navigate} onClose={onClose} authAxios={authAxios} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Main Panel ----
export function AiAssistPanel({ isFullPage = false, onClose, onToggleSize }) {
  const { user, authAxios } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [messages, setMessages] = useState(() => loadFromSession(STORAGE_KEY_MESSAGES, []));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => loadFromSession(STORAGE_KEY_SESSION, `session-${Date.now()}`));
  const [greetingLoading, setGreetingLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const greetingFired = useRef(false);

  // Persist messages and sessionId to sessionStorage
  useEffect(() => { saveToSession(STORAGE_KEY_MESSAGES, messages); }, [messages]);
  useEffect(() => { saveToSession(STORAGE_KEY_SESSION, sessionId); }, [sessionId]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 50);
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  // Auto-greeting on first load (once per session)
  useEffect(() => {
    if (greetingFired.current) return;
    if (messages.length > 0) return;
    const greeted = sessionStorage.getItem(STORAGE_KEY_GREETED);
    if (greeted) return;

    greetingFired.current = true;
    setGreetingLoading(true);

    const fetchGreeting = async () => {
      try {
        const api = authAxios();
        const res = await api.post('/assist', {
          message: '__greeting__',
          session_id: sessionId,
          page_context: getPageContext(),
        });
        const data = res.data;
        if (data.session_id) setSessionId(data.session_id);

        const greetMsg = {
          role: 'assistant',
          content: data.message || '',
          references: data.references || [],
          action_proposal: null,
        };
        setMessages([greetMsg]);
        sessionStorage.setItem(STORAGE_KEY_GREETED, 'true');
      } catch (err) {
        // Silent fail — show empty state instead
      } finally {
        setGreetingLoading(false);
      }
    };
    fetchGreeting();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPageContext = useCallback(() => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard (overzicht)';
    if (path === '/kennisbank') return 'Kennisbank (alle entries)';
    if (path === '/compliance') return 'Compliance Dashboard';
    if (path === '/bestanden') return 'Bestanden overzicht';
    if (path === '/inzichten') return 'AI Inzichten';
    if (path.startsWith('/locatie/')) return `Locatie pagina: ${path.split('/locatie/')[1]}`;
    if (path.startsWith('/kennisbank/')) return 'Kennisbank entry detail';
    return path;
  }, [location.pathname]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const api = authAxios();
      const res = await api.post('/assist', {
        message: text.trim(),
        session_id: sessionId,
        page_context: getPageContext(),
      });
      const data = res.data;
      if (data.session_id) setSessionId(data.session_id);

      const assistMsg = {
        role: 'assistant',
        content: data.message || 'Geen antwoord ontvangen.',
        references: data.references || [],
        action_proposal: data.action_proposal || null,
      };
      setMessages(prev => [...prev, assistMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, er ging iets mis. Controleer of de AI Assistent correct is geconfigureerd.',
        references: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (proposal, msgIndex, confirmed) => {
    if (!confirmed) {
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, action_proposal: null, content: m.content + '\n\nActie geannuleerd.' } : m
      ));
      return;
    }

    setActionLoading(true);
    try {
      let endpoint, method, body;
      const p = proposal.params || {};

      if (proposal.action === 'create_entry') {
        endpoint = '/entries';
        method = 'POST';
        body = {
          title: p.title || 'Nieuw item',
          location_id: p.location_id || 'all',
          category: p.category || '',
          content: p.content || '',
        };
      } else if (proposal.action === 'create_compliance_item') {
        endpoint = '/compliance-items';
        method = 'POST';
        body = {
          title: p.title || 'Nieuw compliance item',
          type: p.type || 'keuring',
          location_id: p.location_id || 'all',
          due_date: p.due_date || new Date(Date.now() + 30 * 86400000).toISOString(),
          assigned_to: user?.email || '',
        };
      } else if (proposal.action === 'complete_compliance_item') {
        const itemId = p.compliance_id || p.id;
        if (!itemId) throw new Error('Geen item ID');
        endpoint = `/compliance-items/${itemId}/complete`;
        method = 'POST';
        body = { notes: p.notes || 'Afgerond via AI Assistent' };
      }

      if (endpoint) {
        const api = authAxios();
        await api({ method, url: endpoint, data: body });
        toast.success('Actie uitgevoerd');
        setMessages(prev => prev.map((m, i) =>
          i === msgIndex ? { ...m, action_proposal: null, content: m.content + '\n\nActie succesvol uitgevoerd.' } : m
        ));
      }
    } catch (err) {
      toast.error('Actie mislukt: ' + (err.response?.data?.detail || err.message));
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, action_proposal: null, content: m.content + '\n\nActie mislukt. Probeer het handmatig.' } : m
      ));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const showEmptyState = messages.length === 0 && !greetingLoading;

  return (
    <div className={`flex flex-col ${isFullPage ? 'h-[calc(100vh-120px)]' : 'h-full'}`} data-testid="assist-panel">
      {/* Header */}
      {!isFullPage && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 dark:border-[#21262D] bg-white/90 dark:bg-[#0D1117]/90 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">TW1 Assistent</h3>
          </div>
          <div className="flex items-center gap-1">
            {onToggleSize && (
              <button onClick={onToggleSize} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors duration-150" data-testid="assist-toggle-size">
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
            {onClose && (
              <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors duration-150" data-testid="assist-close">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-4" ref={scrollRef}>
        {/* Greeting loading indicator */}
        {greetingLoading && messages.length === 0 && (
          <div className="flex justify-start mb-3" data-testid="assist-greeting-loading">
            <div className="bg-white dark:bg-[#161B22] border border-slate-200/80 dark:border-[#30363D] rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {showEmptyState && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12" data-testid="assist-empty-state">
            <div className="w-12 h-12 rounded-2xl bg-[#2E75B6]/10 dark:bg-[#2E75B6]/20 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-[#2E75B6]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9] mb-1">Hoi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</p>
            <p className="text-xs text-slate-500 dark:text-[#8B949E] max-w-[240px]">
              Vraag me iets over locaties, compliance, kennisbank of bestanden.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-1.5">
              {['Wat is te laat?', 'Hoe staat Eindhoven?', 'Zoek facturen'].map(q => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="px-3 py-1.5 text-xs rounded-full border border-slate-200 dark:border-[#30363D] text-slate-600 dark:text-[#8B949E] hover:border-[#2E75B6] hover:text-[#2E75B6] dark:hover:border-[#58A6FF] dark:hover:text-[#58A6FF] transition-colors duration-150"
                  data-testid={`assist-suggestion-${q.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <React.Fragment key={i}>
            <MessageBubble msg={msg} navigate={navigate} onClose={isFullPage ? null : onClose} authAxios={authAxios} />
            {msg.action_proposal && (
              <div className="flex justify-start mb-3 ml-0">
                <div className="max-w-[85%]">
                  <ActionCard
                    proposal={msg.action_proposal}
                    onConfirm={(updatedProposal) => handleAction(updatedProposal, i, true)}
                    onCancel={() => handleAction(msg.action_proposal, i, false)}
                    loading={actionLoading}
                    authAxios={authAxios}
                  />
                </div>
              </div>
            )}
          </React.Fragment>
        ))}

        {loading && (
          <div className="flex justify-start mb-3" data-testid="assist-typing-indicator">
            <div className="bg-white dark:bg-[#161B22] border border-slate-200/80 dark:border-[#30363D] rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-[#484F58] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-slate-200/80 dark:border-[#21262D] bg-white/90 dark:bg-[#0D1117]/90 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Stel een vraag..."
            className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-[#30363D] bg-slate-50 dark:bg-[#161B22] text-slate-900 dark:text-[#C9D1D9] placeholder:text-slate-400 dark:placeholder:text-[#484F58] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors duration-150"
            disabled={loading}
            data-testid="assist-input"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="p-2.5 rounded-xl bg-[#2E75B6] text-white hover:bg-[#2563a0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            data-testid="assist-send-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}
